import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface NavbarPermission {
  navbar_section_id: string;
  team: string;
  is_enabled: boolean;
}

interface NavbarSection {
  id: string;
  name: string;
  display_name: string;
  section_type: string;
  parent_section_id?: string;
  route_path?: string;
}

type RoleLevel = 'user' | 'admin' | 'superadmin' | 'adviser' | null;

export const useNavbarPermissions = (
  userRole: RoleLevel,
  team?: string | null,
) => {
  const [permissions, setPermissions] = useState<NavbarPermission[]>([]);
  const [sections, setSections] = useState<NavbarSection[]>([]);
  const [loading, setLoading] = useState(true);

  const isSuperadmin = userRole === 'superadmin';

  const fetchPermissions = useCallback(async () => {
    if (!userRole) {
      setLoading(false);
      return;
    }
    if (!team) {
      // No team. A superadmin doesn't depend on team-scoped permissions (canAccess bypasses), but
      // still needs the sections list; everyone else simply has no team-scoped permissions. Either
      // way, RESOLVE loading — leaving it true here stranded blank-team users (incl. superadmins) with
      // canAccess=false for everything, forever.
      try {
        if (isSuperadmin) {
          const { data: sectionsData } = await supabase.from('navbar_sections').select('*').order('sort_order');
          setSections(sectionsData || []);
        }
        setPermissions([]);
      } catch (error) {
        console.error('Error fetching navbar sections:', error);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const [sectionsRes, permsRes] = await Promise.all([
        supabase.from('navbar_sections').select('*').order('sort_order'),
        supabase
          .from('navbar_permissions')
          .select('navbar_section_id, team, is_enabled')
          .eq('team', team),
      ]);
      if (sectionsRes.error) throw sectionsRes.error;
      if (permsRes.error) throw permsRes.error;

      setSections(sectionsRes.data || []);
      setPermissions((permsRes.data || []) as NavbarPermission[]);
    } catch (error) {
      console.error('Error fetching navbar permissions:', error);
    } finally {
      setLoading(false);
    }
  }, [userRole, team, isSuperadmin]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') && userRole && session) {
        fetchPermissions();
      } else if (event === 'SIGNED_OUT') {
        setPermissions([]);
        setSections([]);
        setLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [fetchPermissions, userRole]);

  const canAccess = (sectionName: string): boolean => {
    if (isSuperadmin) return true; // Super Admin always has access — even before perms load / with no team
    if (!userRole || loading) return false;

    const section = sections.find((s) => s.name === sectionName);
    if (!section) return false;
    const permission = permissions.find((p) => p.navbar_section_id === section.id);
    return permission?.is_enabled ?? false;
  };

  const getAccessibleSections = (): NavbarSection[] => {
    if (isSuperadmin) return sections; // superadmin sees all sections, independent of the loading gate
    if (!userRole || loading) return [];
    return sections.filter((section) => {
      const permission = permissions.find((p) => p.navbar_section_id === section.id);
      return permission?.is_enabled ?? false;
    });
  };

  return { canAccess, getAccessibleSections, loading, sections, permissions };
};
